import { useMemo, useState, useCallback} from "react";

import useFocusedField from "psims/react/util/use-focused-field";
import useConfirm from "../../shared/use-confirm";
import useMsoAnnualActivityRefData from "../shared/use-mso-annual-activity-ref-data";
import useStorageSites from "../shared/storage-sites/use-storage-sites";
import useImporterAnnualActivities from "./annual-activities/use-importer-annual-activities";
import { UseImporterAnnualActivityAPI } from "./use-importer-annual-activity-api";
import { FocusField } from "../shared/types";
import useImporterAnnualActivityNotifications from "./use-importer-annual-activity-notifications";
import useImporterAnnualActivityProgress from "./use-importer-annual-activity-progress";
import useViewMode, { ViewMode } from "../../shared/use-view-mode";
import useImporterAnnualActivityNavigation from "./use-importer-annual-activity-navigation";
import useSubmit from '../../shared/use-submit';
import useImporterAnnualActivityControls from "./use-importer-annual-activity-controls";
import useMsoComment from "../shared/mso-comment/use-mso-comment";
import { isMsoComment } from "psims/models/submission-types/mso/mso-comment";
import useServiceMessages from "../../shared/use-service-messages";
import { getRecordResults } from "psims/models/submission-types/mso/annual-activity/importer/mso-annual-activity-importer-submission";
import useImporterImports from "../shared/imports/use-importer-imports";
import useClearData from 'psims/react/pages/primary-pages/data-submissions/shared/use-clear-data';
import useMsoStockholdingStorages from '../shared/mso-stockholding-storages/use-mso-stockholding-storages';
import useEnforceableArrangements from "../shared/enforceable-arrangements/use-enforceable-arrangements";
import useAustralianControllingCorporations from "../shared/australian-controlling-corporations/use-australian-controlling-corporations";
import type { UpdateMsoCommentAnnualActivityVM } from 'psims/gen/xapi-client/models/UpdateMsoCommentAnnualActivityVM';
import { recordActionAsEnum } from 'psims/models/api/data-submission-record-action';



const FUEL_CATEGORY_TO_COMMENT_CATEGORY: Record<number, number> = {
    1: 4,  // Gasoline   → Storage - Gasoline
    2: 6,  // Diesel     → Storage - Diesel
    3: 7,  // Kerosene   → Storage - Kerosene
    4: 8,  // Crude      → Storage - Crude
    5: 9,  // Unfinished → Storage - Unfinished
};

interface UseImporterAnnualActivityProps {
  apiCtrl: PopulatedProps<UseImporterAnnualActivityAPI, 'submission'>;
  
}

function useImporterAnnualActivity({apiCtrl}: UseImporterAnnualActivityProps) {
  const viewMode = useViewMode(apiCtrl.submission);

  const refData = useMsoAnnualActivityRefData({dataSubmission: apiCtrl.submission.dataSubmission});

  const progressCtrl = useImporterAnnualActivityProgress({apiCtrl, viewMode});

  const focusFieldCtrl = useFocusedField<FocusField>();

  const deleteCtrl = useConfirm();
  // ── Stable initialData refs (prevent Symbol re-stamping on every render) ──
const stableInitialStorages = useMemo(
  () => (apiCtrl.submission.msoStockholdingStorages || []).map(s => ({
    ...s,
    _id: Symbol(),
    _key: `row_${s.id ?? Math.random()}`,
    recordAction: undefined,
  })),
  [apiCtrl.submission.msoStockholdingStorages]
);

const stableInitialImports = useMemo(
  () => apiCtrl.submission.imports,
  [apiCtrl.submission.imports]
);

const stableInitialEA = useMemo(
  () => (apiCtrl.submission.enforceableArrangements ?? []).map(e => ({
    ...e,
    _id: Symbol(),
    _key: `ea_${e.id ?? Math.random()}`,
  })),
  [apiCtrl.submission.enforceableArrangements]
);

const stableInitialACC = useMemo(
  () => (apiCtrl.submission.australianControllingCorporations ?? []).map(a => ({
    ...a,
    _id: Symbol(),
    _key: `acc_${a.id ?? Math.random()}`,
  })),
  [apiCtrl.submission.australianControllingCorporations]
);


 const msoStockholdingStorageCtrl = useMsoStockholdingStorages({
  dataSubmissionId: apiCtrl.submission.dataSubmission.id,
  focusFieldCtrl,
initialStorages: (apiCtrl.submission.msoStockholdingStorages || []).map(s => ({
  ...s,
  _id: Symbol(),
  _key: `row_${s.id ?? Math.random()}`,
  recordAction: undefined
})),
  progressCtrl,
  refData
});
  const importsCtrl = useImporterImports({
    dataSubmissionId: apiCtrl.submission.dataSubmission.id,
    focusFieldCtrl,
    initialData: apiCtrl.submission.imports,
    progressCtrl,
    refData,
  });

  const importsData = useMemo(
  () => importsCtrl.updateImports.map(i => i.data),
  [importsCtrl.updateImports]
);
console.log('apiCtrl.submission.msoTargetDays:', apiCtrl.submission.msoTargetDays);

const annualActivitiesCtrl = useImporterAnnualActivities({
  dataSubmissionId: apiCtrl.submission.dataSubmission.id,
  focusFieldCtrl,
  imports: importsData, // ✅ stable reference
  initialAnnualActivities: apiCtrl.submission.annualActivities,
  progressCtrl,
  refData,
  msoTargetDays: apiCtrl.submission.msoTargetDays ?? [],
 
});

  const commentsCtrl = useMsoComment({
    dataSubmissionId: apiCtrl.submission.dataSubmission.id,
    focusFieldCtrl,
    initialComment: isMsoComment(apiCtrl.submission.msoComment) ? apiCtrl.submission.msoComment : null,
    isRequired: annualActivitiesCtrl.requiresComments,
    progressCtrl,
  });

  const enforceableArrangementsCtrl = useEnforceableArrangements({
  initial: (apiCtrl.submission.enforceableArrangements ?? []).map(e => ({
    ...e,
    _id: Symbol(),
    _key: `ea_${e.id ?? Math.random()}`,
  })),
  dataSubmissionId: apiCtrl.submission.dataSubmission.id,
  focusFieldCtrl,
  products: refData.products,
});

 const australianControllingCorporationsCtrl = useAustralianControllingCorporations(
  (apiCtrl.submission.australianControllingCorporations ?? []).map(a => ({
    ...a,
    _id: Symbol(),
    _key: `acc_${a.id ?? Math.random()}`,
  })),
  apiCtrl.submission.dataSubmission.id,
  focusFieldCtrl,  // ← add this
);
  
  const serviceMessages = useServiceMessages({
    response: apiCtrl.updateResponse,
    responseStatus: apiCtrl.loadStatus,
    serverError: apiCtrl.updateError,
    extractRecordResults: getRecordResults
  });

  const anyInvalid = useMemo(() => {
    return (
      annualActivitiesCtrl.anyInvalid ||
      importsCtrl.anyInvalid ||
      msoStockholdingStorageCtrl.anyInvalid ||
      australianControllingCorporationsCtrl.anyInvalid 
    );
  }, [annualActivitiesCtrl, importsCtrl, msoStockholdingStorageCtrl, australianControllingCorporationsCtrl]);

  const submitCtrl = useSubmit({
    apiCtrl,
    dataSubmission: apiCtrl.submission.dataSubmission,
    focusFieldCtrl,
    hasExternalErrors: anyInvalid,
  });

  const pageChanges = useMemo(() => {
    return [
      // data page
      [
        msoStockholdingStorageCtrl.hasChanges,
        annualActivitiesCtrl.hasChanges,
        importsCtrl.hasChanges,
        commentsCtrl.hasChanges
      ],
      // submit page
      [submitCtrl.hasUnsavedChanges]
    ]
  }, [
    annualActivitiesCtrl.hasChanges,
    commentsCtrl.hasChanges,
    importsCtrl.hasChanges,
    msoStockholdingStorageCtrl.hasChanges,
    submitCtrl.hasUnsavedChanges,
  ]);
const [commentUpdates, setCommentUpdates] = useState<Record<number, string>>({});

  const handleCommentChange = useCallback((fuelCategory: number, comment: string) => {
    setCommentUpdates(prev => ({ ...prev, [fuelCategory]: comment }));
  }, []);
const [importsComment, setImportsComment] = useState<string>(
    () => apiCtrl.submission.msoCommentAnnualActivities
        ?.find(c => c.commentCategoryType === 1)
        ?.reporterComments ?? ''
);

const handleImportsCommentChange = useCallback((comment: string) => {
    setImportsComment(comment);
}, []);

const [enforceableArrangementsComment, setEnforceableArrangementsComment] = useState<string>(
    () => apiCtrl.submission.msoCommentAnnualActivities
        ?.find(c => c.commentCategoryType === 10)
        ?.reporterComments ?? ''
);

const handleEnforceableArrangementsCommentChange = useCallback((comment: string) => {
    setEnforceableArrangementsComment(comment);
}, []);

const [australianControllingCorpsComment, setAustralianControllingCorpsComment] = useState<string>(
    () => apiCtrl.submission.msoCommentAnnualActivities
        ?.find(c => c.commentCategoryType === 11)
        ?.reporterComments ?? ''
);

const handleAustralianControllingCorpsCommentChange = useCallback((comment: string) => {
    setAustralianControllingCorpsComment(comment);
}, []);

  // Merge updates into the initial list for the save payload
  const msoCommentAnnualActivities = useMemo(() => {
    const base = apiCtrl.submission.msoCommentAnnualActivities ?? [];
    const dataSubmissionId = apiCtrl.submission.dataSubmission.id;

    // Storage comments — identified by msoStorageFuelCategoryType (1-5)
   const updated = base
    .filter(c => c.msoStorageFuelCategoryType != null && c.msoStorageFuelCategoryType in commentUpdates)
    .map(c => ({
        ...c,
        dataSubmissionId,
        commentCategoryType: FUEL_CATEGORY_TO_COMMENT_CATEGORY[c.msoStorageFuelCategoryType!],
        reporterComments: commentUpdates[c.msoStorageFuelCategoryType!],
        recordAction: 2,
    }));

    const existingFuelCategories = new Set(base
        .filter(c => c.msoStorageFuelCategoryType != null)
        .map(c => c.msoStorageFuelCategoryType));

    const newStorageComments = Object.entries(commentUpdates)
    .filter(([fc]) => !existingFuelCategories.has(Number(fc)))
    .map(([fc, reporterComments]) => ({
        dataSubmissionId,
        commentCategoryType: FUEL_CATEGORY_TO_COMMENT_CATEGORY[Number(fc)],
        msoStorageFuelCategoryType: Number(fc),
        reporterComments,
        recordAction: 1,
    }));

    // Imports comment — commentCategoryType: 1, msoStorageFuelCategoryType: null
    const existingImportsComment = base.find(c => c.commentCategoryType === 1);
    const importsCommentEntry = importsComment !== ''
    ? existingImportsComment
        ? {
            ...existingImportsComment,
            dataSubmissionId,
            msoStorageFuelCategoryType: undefined,  // ← explicitly undefined, don't spread null
            reporterComments: importsComment,
            recordAction: recordActionAsEnum('Update'),
          }
        : {
            dataSubmissionId,
            commentCategoryType: 1,
            msoStorageFuelCategoryType: undefined,
            reporterComments: importsComment,
            recordAction: recordActionAsEnum('Create'),
          }
    : null;
    // Enforceable arrangements comment — commentCategoryType: 10
    const existingEAComment = base.find(c => c.commentCategoryType === 10);
    const eaCommentEntry = enforceableArrangementsComment !== ''
        ? existingEAComment
            ? {
                ...existingEAComment,
                dataSubmissionId,
                msoStorageFuelCategoryType: undefined,
                reporterComments: enforceableArrangementsComment,
                recordAction: recordActionAsEnum('Update'),
              }
            : {
                dataSubmissionId,
                commentCategoryType: 10,
                msoStorageFuelCategoryType: undefined,
                reporterComments: enforceableArrangementsComment,
                recordAction: recordActionAsEnum('Create'),
              }
        : null;
    const existingACCComment = base.find(c => c.commentCategoryType === 11);
const accCommentEntry = australianControllingCorpsComment !== ''
    ? existingACCComment
        ? {
            ...existingACCComment,
            dataSubmissionId,
            msoStorageFuelCategoryType: undefined,
            reporterComments: australianControllingCorpsComment,
            recordAction: recordActionAsEnum('Update'),
          }
        : {
            dataSubmissionId,
            commentCategoryType: 11,
            msoStorageFuelCategoryType: undefined,
            reporterComments: australianControllingCorpsComment,
            recordAction: recordActionAsEnum('Create'),
          }
    : null;    
return [
    ...updated,
    ...newStorageComments,
    ...(importsCommentEntry ? [importsCommentEntry] : []),
    ...(eaCommentEntry ? [eaCommentEntry] : []),
    ...(accCommentEntry ? [accCommentEntry] : []),   // ← add
].map(c => ({
    ...c,
    msoStorageFuelCategoryType: c.msoStorageFuelCategoryType ?? undefined,
})) as UpdateMsoCommentAnnualActivityVM[];
}, [apiCtrl.submission.msoCommentAnnualActivities, commentUpdates, importsComment, enforceableArrangementsComment]);


  

  const navCtrl = useImporterAnnualActivityNavigation({
    annualActivitiesCtrl, 
    apiCtrl, 
    commentsCtrl, 
    importsCtrl,
    pageChanges, 
    progressCtrl, 
    msoStockholdingStorageCtrl,
    enforceableArrangementsCtrl, 
    australianControllingCorporationsCtrl,  
    submitCtrl, 
    viewMode,
    msoCommentAnnualActivities
  });

  const notificationsCtrl = useImporterAnnualActivityNotifications({
    annualActivitiesCtrl,
    commentsCtrl,
    importsCtrl,
    navCtrl,
    progressCtrl,
    serviceMessages,
    msoStockholdingStorageCtrl,
    enforceableArrangementsCtrl,
    australianControllingCorporationsCtrl,
    submitCtrl,
  });

  const controlsCtrl = useImporterAnnualActivityControls({apiCtrl, navCtrl, progressCtrl, viewMode});

  const clearDataCtrl = useClearData({
      onConfirm: () => {
          apiCtrl.clearAllData();
          submitCtrl.updateDeclaration(false);
      },
      submission: apiCtrl.submission?.dataSubmission,
  });

  // ── Comment updates keyed by msoStorageFuelCategoryType ──────────────────
 return useMemo(() => ({
    annualActivitiesCtrl,
    apiCtrl,
    clearDataCtrl,
    commentsCtrl,
    controlsCtrl,
    deleteCtrl,
    focusFieldCtrl,
    isDisabled: viewMode === 'view',
    importsCtrl,
    navCtrl,
    notificationsCtrl,
    progressCtrl,
    refData,
    submitCtrl,
    msoStockholdingStorageCtrl,
    enforceableArrangementsCtrl,
    australianControllingCorporationsCtrl,
    viewMode: viewMode as ViewMode,
    handleCommentChange,
    handleImportsCommentChange,
    handleEnforceableArrangementsCommentChange,
    handleAustralianControllingCorpsCommentChange,
    msoCommentAnnualActivities,
    msoTargetDays: apiCtrl.submission.msoTargetDays ?? [],
}), [
    annualActivitiesCtrl,
    apiCtrl,
    clearDataCtrl,
    commentsCtrl,
    controlsCtrl,
    deleteCtrl,
    focusFieldCtrl,
    importsCtrl,
    navCtrl,
    notificationsCtrl,
    progressCtrl,
    refData,
    submitCtrl,
    msoStockholdingStorageCtrl,
    enforceableArrangementsCtrl,
    australianControllingCorporationsCtrl,
    viewMode,
    handleCommentChange,
    handleImportsCommentChange,
    handleEnforceableArrangementsCommentChange,
    handleAustralianControllingCorpsCommentChange,
    msoCommentAnnualActivities,  // ← this already recomputes when australianControllingCorpsComment changes
]);
}
export default useImporterAnnualActivity
